<script setup lang="ts">
  import { useQuery } from '@tanstack/vue-query'
  import { onKeyStroke, useScrollLock } from '@vueuse/core'
  import { compareDesc } from 'date-fns'
  import { useData } from 'vike-vue/useData'
  import {
    computed,
    onMounted,
    onServerPrefetch,
    reactive,
    ref,
    toRaw,
    useTemplateRef,
    watchEffect,
  } from 'vue'
  import DiscordIcon from '~icons/fa7-brands/discord'
  import PatreonIcon from '~icons/fa7-brands/patreon'
  import TwitchIcon from '~icons/fa7-brands/twitch'
  import AccountLinkingModal from '~renderer/components/AccountLinkingModal.vue'
  import BannerAd from '~renderer/components/ads/BannerAd.vue'
  import ConfirmBanModal from '~renderer/components/ConfirmBanModal.vue'
  import ConfirmBlockModal from '~renderer/components/ConfirmBlockModal.vue'
  import Footer from '~renderer/components/Footer.vue'
  import Header from '~renderer/components/Header.vue'
  import ReportIslandModal from '~renderer/components/ReportIslandModal.vue'
  import StarRatingDisplay from '~renderer/components/StarRatingDisplay.vue'
  import UserHostCard from '~renderer/components/UserHostCard.vue'
  import UserVisitCard from '~renderer/components/UserVisitCard.vue'
  import { useAuth } from '~renderer/composables/useAuth'
  import { useIslandTime } from '~renderer/composables/useIslandTime'
  import { guard } from '~renderer/util/telefunc'
  import { onGetOpenIslandsByHostId } from '~rpc/procedures/island/onGetOpenIslandsByHostId.telefunc'
  import { onReportIsland } from '~rpc/procedures/island/onReportIsland.telefunc'
  import { onBanUserById } from '~rpc/procedures/user/onBanUserById.telefunc'
  import { onBlockUserById } from '~rpc/procedures/user/onBlockUserById.telefunc'
  import { onGetUserVisitHistory } from '~rpc/procedures/user/onGetUserVisitHistory.telefunc'
  import { onLockUserById } from '~rpc/procedures/user/onLockUserById.telefunc'
  import { onUnblockUserById } from '~rpc/procedures/user/onUnblockUserById.telefunc'
  import { onUnlockUserById } from '~rpc/procedures/user/onUnlockUserById.telefunc'
  import type { SocialProviderId } from '~shared/types/auth'
  import * as AttemptUtil from '~shared/util/attempt'
  import { isAuthenticatedUser } from '~shared/util/user'
  import type { UserPageData } from './+data'

  type Modals = {
    reportIsland:
      | false
      | {
          islandId: string
          name: string
          visitId: string
        }
    accountLinkingOpen:
      | false
      | {
          mode: 'link' | 'unlink'
          targetProviderId: SocialProviderId
        }
    confirmBan:
      | false
      | {
          userId: string
          name: string
        }
    confirmBlock:
      | false
      | {
          userId: string
          name: string
        }
  }
  // MARK: Pure Composables
  const {
    userIsBlocked,
    userIsGranted,
    userStats,
    userAccounts,
    twitchIsLive,
    twitchLink,
    twitchName,
  } = useData<UserPageData>()
  const { user, isAdmin, isModerator } = useAuth()
  const { formatDuration } = useIslandTime()
  const isScrollLocked = useScrollLock(typeof window !== 'undefined' ? document.body : null)
  const visitListEl = useTemplateRef<HTMLDivElement>('visitListEl')

  // MARK: State
  const modals = reactive<Modals>({
    reportIsland: false,
    accountLinkingOpen: false,
    confirmBan: false,
    confirmBlock: false,
  })
  const blocked = ref(false)
  const granted = ref(false)

  // MARK: Computed
  const modalIsOpen = computed(() => Object.values(modals).some(Boolean))
  const isUserStats = computed(() => user.value?.id === userStats?.id)
  const canBanUsers = computed(() => isAdmin.value || isModerator.value)
  const canGrantUsers = computed(() => isAdmin.value || isModerator.value)

  // need to use toRaw since pastVisits.value is readonly and
  // .sort must mutate the original array in order to work
  const visitsSorted = computed(() =>
    toRaw(pastVisits.value ?? []).sort((a, b) => compareDesc(a.visitedAt, b.visitedAt)),
  )
  const attachedProviders = computed(() => userAccounts?.map((account) => account.providerId) || [])
  const twitchAttached = computed(() => attachedProviders.value.includes('twitch'))
  const discordAttached = computed(() => attachedProviders.value.includes('discord'))
  const patreonAttached = computed(() => attachedProviders.value.includes('patreon'))

  const providers = computed(
    () =>
      [
        {
          id: 'discord',
          name: 'Discord',
          icon: DiscordIcon,
          isConnected: discordAttached.value,
          colors: `${discordAttached.value ? 'text-[#5865F2]' : 'text-white/20'} hover:text-[#4752c4]`,
        },
        {
          id: 'twitch',
          name: 'Twitch',
          icon: TwitchIcon,
          isConnected: twitchAttached.value,
          colors: `${twitchAttached.value ? 'text-[#9146FF]' : 'text-white/20'} hover:text-[#7d3bd9]`,
        },
        {
          id: 'patreon',
          name: 'Patreon',
          icon: PatreonIcon,
          isConnected: patreonAttached.value,
          colors: `${patreonAttached.value ? 'text-[#FF424D]' : 'text-white/20'} hover:text-[#E63B45]`,
        },
      ] as const,
  )

  // MARK: Dependent Composables
  const { data: pastVisits, suspense } = useQuery({
    queryKey: ['past-visits', userStats?.id],
    queryFn: async () => {
      if (!user.value || !isAuthenticatedUser(user.value) || !userStats?.id) return []

      const visits = await guard(
        onGetUserVisitHistory({
          userId: userStats.id,
        }),
      )

      return visits
    },
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  const { data: hostedIslands, suspense: hostedIslandSuspense } = useQuery({
    queryKey: ['hosted-island', userStats?.id],
    queryFn: async () => {
      if (!userStats?.id) return null

      const islands = await guard(
        onGetOpenIslandsByHostId({
          hostId: userStats.id,
        }),
      )

      return islands
    },
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  // MARK: Methods
  const onReportIslandClicked = async (visitId: string) => {
    const visit = pastVisits.value?.find((v) => v.visitId === visitId)
    if (!visit) return
    if (visit.islandSnapshot === null) return
    const {
      islandId,
      islandSnapshot: { name },
    } = visit
    modals.reportIsland = { islandId, name, visitId }
  }

  const onReportIslandConfirmed = async (
    islandId: string,
    visitId: string,
    reportType: string,
    comment: string,
  ) => {
    const [err] = await AttemptUtil.async(() =>
      guard(onReportIsland({ islandId, visitId, reportType, comment })),
    )
    if (err) console.error(err)

    modals.reportIsland = false
  }

  const onReportIslandCancelled = async () => {
    modals.reportIsland = false
  }

  const onAccountLinkingClicked = async (
    mode: 'link' | 'unlink',
    targetProviderId: SocialProviderId,
  ) => {
    modals.accountLinkingOpen = { mode, targetProviderId }
  }

  const onAccountLinkingCancelled = async () => {
    modals.accountLinkingOpen = false
  }

  const onBanUserCancelled = async () => {
    modals.confirmBan = false
  }

  const onBanUserRequested = async (userId: string, name: string) => {
    modals.confirmBan = { userId, name }
  }

  const onBanUserConfirmed = async () => {
    if (!modals.confirmBan) return
    const { userId } = modals.confirmBan
    const [err] = await AttemptUtil.async(() => guard(onBanUserById({ userId })))

    if (err) throw err

    modals.confirmBan = false
  }

  const onBlockUserCancelled = async () => {
    modals.confirmBlock = false
  }

  const onBlockUserRequested = async (userId: string, name: string) => {
    modals.confirmBlock = { userId, name }
  }

  const onBlockUserConfirmed = async () => {
    if (!modals.confirmBlock) return
    const { userId } = modals.confirmBlock
    const [err] = await AttemptUtil.async(() => guard(onBlockUserById({ userId })))

    if (err) throw err

    blocked.value = true
    modals.confirmBlock = false
  }

  const onUnblockUserConfirmed = async (userId: string) => {
    const [err] = await AttemptUtil.async(() => guard(onUnblockUserById({ userId })))
    if (err) throw err

    blocked.value = false
  }

  const onLockUserConfirmed = async () => {
    if (!userStats || !userStats.id) return
    const userId = userStats.id
    const [err] = await AttemptUtil.async(() => guard(onLockUserById({ userId })))

    if (err) throw err

    granted.value = false
  }

  const onUnlockUserConfirmed = async () => {
    if (!userStats || !userStats.id) return
    const userId = userStats.id
    const [err] = await AttemptUtil.async(() => guard(onUnlockUserById({ userId })))
    if (err) throw err

    granted.value = true
  }

  // MARK: Setup
  watchEffect(() => {
    const { reportIsland, accountLinkingOpen } = modals
    isScrollLocked.value = !!reportIsland && !!accountLinkingOpen
  })

  onKeyStroke('Escape', () => {
    modals.reportIsland = false
    modals.accountLinkingOpen = false
  })

  onMounted(() => {
    blocked.value = userIsBlocked
    granted.value = userIsGranted
  })

  onServerPrefetch(async () => Promise.all([suspense, hostedIslandSuspense]))
</script>

<template>
  <div class="bg-tan-900 flex flex-col font-sans text-white lg:min-h-screen">
    <Header />

    <div
      v-if="modalIsOpen"
      class="pointer-events-auto fixed inset-0 z-10 flex h-screen w-screen items-center justify-center bg-purple-500/80"
      @click.self="
        () => {
          modals.reportIsland = false
          modals.accountLinkingOpen = false
        }
      "
    >
      <ReportIslandModal
        v-if="modals.reportIsland"
        :visit="modals.reportIsland"
        @confirm="onReportIslandConfirmed"
        @cancel="onReportIslandCancelled"
      />
      <ConfirmBanModal
        v-if="modals.confirmBan"
        :user="modals.confirmBan"
        @confirm="onBanUserConfirmed"
        @cancel="onBanUserCancelled"
      />
      <ConfirmBlockModal
        v-if="modals.confirmBlock"
        :user="modals.confirmBlock"
        @confirm="onBlockUserConfirmed"
        @cancel="onBlockUserCancelled"
      />
      <AccountLinkingModal
        v-if="modals.accountLinkingOpen"
        :mode="modals.accountLinkingOpen.mode"
        :target-provider-id="modals.accountLinkingOpen.targetProviderId"
        @close="onAccountLinkingCancelled"
      />
    </div>

    <main class="flex flex-1 flex-col lg:flex-row" v-if="userStats">
      <!-- Left Column -->
      <div
        class="bg-tan-800 motion-safe:bg-pan-12 max-w-8xl relative w-full bg-[url('/images/userbg.png')] bg-size-[auto_10rem] bg-repeat-x"
      >
        <div
          class="flex flex-col gap-6 border-l-8 border-yellow-400 p-4 md:border-l-[1.5rem] md:px-12 md:py-8"
        >
          <!-- Header -->
          <div class="flex flex-col items-center gap-4">
            <img
              :src="userStats.image || ''"
              class="mt-8 size-30 rounded-full border-3 border-yellow-400"
              :alt="userStats.name"
            />

            <h1 class="font-display text-4xl font-bold text-white">{{ userStats.name }}</h1>

            <StarRatingDisplay :rating="userStats.rating || 0" />
            <div v-if="twitchLink">
              <a
                :href="twitchLink"
                target="_blank"
                class="group relative flex w-full cursor-pointer items-center justify-center gap-3 overflow-hidden rounded-lg bg-[#9146FF] px-4 py-2 font-semibold text-white transition-all hover:bg-[#7d3bd9] hover:shadow-lg hover:shadow-[#9146FF]/20 active:scale-[0.98]"
              >
                <span
                  ><TwitchIcon class="mr-2 inline" />{{ twitchName
                  }}{{ twitchIsLive ? ' [LIVE]' : '' }}</span
                >
              </a>
            </div>
            <div v-if="canBanUsers">
              <button
                v-if="canBanUsers && userStats.id && userStats.name"
                @click="onBanUserRequested(userStats.id, userStats.name)"
                class="corner-squircle border-base-orange bg-base-purple-500 text-md text-base-orange hover:bg-base-orange flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border px-2.5 py-2 font-semibold transition-all hover:text-purple-500"
              >
                <i-fa7-solid-circle-xmark class="size-6" />
                <p>Ban User</p>
              </button>
            </div>
            <div v-if="canGrantUsers">
              <button
                v-if="granted && userStats.id"
                @click="onLockUserConfirmed()"
                class="corner-squircle border-base-orange bg-base-purple-500 text-md text-base-orange hover:bg-base-orange flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border px-2.5 py-2 font-semibold transition-all hover:text-purple-500"
              >
                <i-fa7-solid-circle-xmark class="size-6" />
                <p>Revoke Benefits</p>
              </button>
              <button
                v-else-if="!granted && userStats.id && userStats.name"
                @click="onUnlockUserConfirmed()"
                class="corner-squircle border-base-green bg-base-purple-500 text-md text-base-green hover:bg-base-green flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border px-2.5 py-2 font-semibold transition-all hover:text-purple-500"
              >
                <i-fa7-solid-circle-check class="size-6" />
                <p>Grant Benefits</p>
              </button>
            </div>
            <div v-if="user?.id && userStats.id !== user.id">
              <button
                v-if="!blocked && userStats.id && userStats.name"
                @click="onBlockUserRequested(userStats.id, userStats.name)"
                class="corner-squircle border-base-orange bg-base-purple-500 text-md text-base-orange hover:bg-base-orange flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border px-2.5 py-2 font-semibold transition-all hover:text-purple-500"
              >
                <i-fa7-solid-circle-xmark class="size-6" />
                <p>Block User</p>
              </button>
              <button
                v-else-if="blocked && userStats.id"
                @click="onUnblockUserConfirmed(userStats.id)"
                class="corner-squircle border-base-orange bg-base-purple-500 text-md text-base-orange hover:bg-base-orange flex cursor-pointer items-center justify-center gap-2 self-stretch rounded-lg border px-2.5 py-2 font-semibold transition-all hover:text-purple-500"
              >
                <i-fa7-solid-circle-check class="size-6" />
                <p>Unblock User</p>
              </button>
            </div>
          </div>

          <!-- Host and Visit Stats -->
          <div
            class="flex flex-col items-stretch justify-center gap-4 align-middle lg:grid-cols-2 xl:flex-row"
          >
            <!-- TODO  -->
            <div
              class="mx-auto flex w-60 flex-col gap-2 rounded-2xl bg-[#2d262f] px-4 py-3 xl:mx-0"
            >
              <div
                class="flex flex-col gap-2 bg-[url('/images/housebg.svg')] bg-center bg-no-repeat py-3"
              >
                <div class="text-center text-4xl font-semibold">
                  {{ userStats.totalHosted || 0 }}
                </div>
                <div class="text-center font-semibold">Islands Hosted</div>
              </div>
              <div class="flex flex-col gap-2 text-xs">
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-te-crowd class="size-5" />
                  <div class="truncate text-slate-100">Total Visitors</div>
                  <div class="text-right font-bold">{{ userStats.totalVisitors || 0 }}</div>
                </div>
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-te-visitor class="size-5" />
                  <div class="truncate text-slate-100">Unique Visitors</div>
                  <div class="text-right font-bold">{{ userStats.uniqueVisitors || 0 }}</div>
                </div>
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-fa7-solid-clock class="size-5" />
                  <div class="truncate text-slate-100">Avg Duration</div>
                  <div class="text-right font-bold">
                    {{ formatDuration(Math.round(userStats.avgTimeOnIsland || 0)) }}
                  </div>
                </div>
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-fa7-solid-hourglass-start class="size-5" />
                  <div class="truncate text-slate-100">Avg Queue Time</div>
                  <div class="text-right font-bold">
                    {{ formatDuration(Math.round(userStats.avgTimeQueued || 0)) }}
                  </div>
                </div>
              </div>
            </div>

            <div
              class="mx-auto flex w-60 flex-col gap-2 rounded-2xl bg-[#2d262f] px-4 py-3 xl:mx-0"
            >
              <div
                class="flex flex-col gap-2 bg-[url('/images/planebg.svg')] bg-center bg-no-repeat py-3"
              >
                <div class="text-center text-4xl font-semibold">
                  {{ userStats.totalVisited || 0 }}
                </div>
                <div class="text-center font-semibold">Islands Visited</div>
              </div>
              <div class="flex flex-col gap-2 text-xs">
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-te-crowd class="size-5" />
                  <div class="truncate text-slate-100">Total Visits</div>
                  <div class="text-right font-bold">{{ userStats.totalVisits || 0 }}</div>
                </div>
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-fa7-solid-star class="size-5" />
                  <div class="truncate text-slate-100">Ratings</div>
                  <div class="text-right font-bold">{{ userStats.totalRatings || 0 }}</div>
                </div>
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-fa7-solid-clock class="size-5 -rotate-45" />
                  <div class="truncate text-slate-100">Avg Duration</div>
                  <div class="text-right font-bold">
                    {{ formatDuration(Math.round(userStats.avgVisitTime || 0)) }}
                  </div>
                </div>
                <div class="grid grid-cols-[1.2rem_1fr_4rem] items-center gap-2">
                  <i-fa7-solid-hourglass-start class="size-5" />
                  <div class="truncate text-slate-100">Time Queued</div>
                  <div class="text-right font-bold">
                    {{ formatDuration(Math.round(userStats.totalQueueTime || 0)) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Social Links -->
          <div
            v-if="isUserStats"
            class="flex flex-row items-center justify-center gap-6 pt-6 text-white"
          >
            <!-- TODO -->
            <template v-for="provider in providers" :key="provider.id">
              <button
                @click="
                  onAccountLinkingClicked(provider.isConnected ? 'unlink' : 'link', provider.id)
                "
                :class="[provider.colors, 'cursor-pointer']"
              >
                <component :is="provider.icon" class="size-10" />
              </button>
            </template>
          </div>
          <div v-if="isUserStats" class="text-center">
            <span class="text-base-tan">Manage Connected Accounts</span>
          </div>
        </div>
      </div>

      <!-- Right Column: Travel Log -->
      <div
        class="flex max-h-[calc(100%-5rem-4rem)] min-h-0 flex-col gap-4 bg-purple-500 px-6 pt-18 pb-6 md:min-w-120"
      >
        <div class="flex flex-row gap-2 text-center">
          <h2 class="font-display flex flex-row gap-2 text-center text-4xl font-bold text-white">
            <i-fa7-solid-plane class="-rotate-45" />
            <div>Travel Log</div>
          </h2>
        </div>

        <div v-if="hostedIslands && hostedIslands.length" class="justify-center text-center">
          <hr class="border-base-tan" />
          <div class="-mt-3">
            <span class="bg-purple-500 px-3 font-medium">Hosting</span>
          </div>
        </div>
        <div class="mb-2 flex flex-col gap-4">
          <UserHostCard
            v-for="hostedIsland in hostedIslands"
            :key="hostedIsland.islandId"
            :island="hostedIsland"
            class="flex text-lg"
          />
        </div>

        <div v-if="visitsSorted && visitsSorted.length" class="justify-center text-center">
          <hr class="border-base-tan" />
          <div class="-mt-3">
            <span class="bg-purple-500 px-3 font-medium">Visited</span>
          </div>
        </div>
        <div
          ref="visitListEl"
          class="max-h-112 flex-1 space-y-4 overflow-y-auto lg:max-h-[calc(100vh-42rem)] lg:min-h-102"
          :class="{ 'pr-3': visitListEl && visitListEl.scrollHeight > visitListEl.clientHeight }"
        >
          <UserVisitCard
            v-for="visit in visitsSorted"
            :visit="visit"
            :key="visit.visitId"
            @report="onReportIslandClicked"
            class="flex text-lg"
          />
        </div>
      </div>
    </main>
    <!-- Ad Banner Area -->
    <div class="bg-tan-200 flex w-full justify-center px-6 py-12">
      <BannerAd />
    </div>

    <Footer />
  </div>
</template>
